import type { GenericSchema } from 'valibot'
import { datadogRum } from '@datadog/browser-rum'
import { consola } from 'consola'
import { any, array, fallback, pipe, safeParse, transform } from 'valibot'

/**
 * Safely parse an array of items. This will remove invalid items and return an empty array if the input is null or undefined.
 *
 * @param schema - The schema to use for each item in the array
 * @returns A schema that parses an array of items, removing invalid items and returning an empty array if the input is null or undefined
 */
export function safeArray<const TSchema extends GenericSchema>(schema: TSchema) {
  return fallback(
    pipe(
      array(
        pipe(
          any(),
          transform((value) => {
            const res = safeParse(schema, value)
            if (!res.success) {
              const logger = (import.meta.dev || import.meta.server) ? consola.error : datadogRum.addError
              logger('Invalid item in safe array', { issues: res.issues })
              return null
            }

            return res.output
          }),
        ),
      ),
      transform(arr => arr.filter((item): item is NonNullable<typeof item> => item !== null)),
    ),
    [],
  )
}
